import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn.js';
import { Input } from './input.js';
/**
 * Time of day as a 24-hour `HH:MM` field, whatever the browser's locale.
 *
 * The native `type="time"` and `type="datetime-local"` inputs draw their own
 * chrome in the operating system's locale, so an en-US browser shows AM/PM no
 * matter what the page says. Corteh consoles are 24-hour everywhere (owner,
 * 2026-10-15); these two controls are the only time inputs the consoles use.
 */
const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
/** `"930"` → `"09:30"`, `"2100"` → `"21:00"`, `"9:5"` stays invalid; `null` when it is not a time yet. */
export function normalizeTime(text) {
    const trimmed = text.trim();
    if (TIME.test(trimmed))
        return trimmed;
    const parts = /^(\d{1,2}):(\d{2})$/.exec(trimmed) ?? /^(\d{1,2})(\d{2})$/.exec(trimmed);
    if (!parts)
        return null;
    const candidate = `${parts[1].padStart(2, '0')}:${parts[2]}`;
    return TIME.test(candidate) ? candidate : null;
}
/** A 24-hour `HH:MM` text field (see the module comment for why not `type="time"`). */
export const TimeInput = React.forwardRef(({ value, onChange, onBlur, className, placeholder, ...props }, ref) => {
    const shown = value.slice(0, 5);
    const [text, setText] = React.useState(shown);
    // A new value from outside replaces the draft unless the draft already means it.
    const [lastShown, setLastShown] = React.useState(shown);
    if (shown !== lastShown) {
        setLastShown(shown);
        if (normalizeTime(text) !== shown)
            setText(shown);
    }
    return (_jsx(Input, { ref: ref, type: "text", inputMode: "numeric", autoComplete: "off", maxLength: 5, placeholder: placeholder ?? 'HH:MM', className: cn('tnum', className), value: text, onChange: (event) => {
            const next = event.target.value.replace(/[^\d:]/g, '');
            setText(next);
            const time = TIME.test(next) ? next : null;
            if (time && time !== shown)
                onChange(time);
        }, onBlur: (event) => {
            const time = normalizeTime(text);
            if (time) {
                setText(time);
                if (time !== shown)
                    onChange(time);
            }
            else {
                setText(shown);
            }
            onBlur?.(event);
        }, ...props }));
});
TimeInput.displayName = 'TimeInput';
/**
 * A day and a 24-hour time as one local `YYYY-MM-DDTHH:MM` value: the drop-in
 * for `type="datetime-local"`, whose time half would follow the browser's
 * 12-hour locale.
 */
export function DateTimeInput({ value, onChange, min, max, id, disabled, invalid, className, 'aria-label': ariaLabel, 'aria-describedby': describedBy, }) {
    const { t } = useTranslation();
    const [datePart = '', timePart = ''] = value.split('T');
    const [date, setDate] = React.useState(datePart);
    const [time, setTime] = React.useState(timePart.slice(0, 5));
    // A new value from outside replaces both drafts.
    const [lastValue, setLastValue] = React.useState({ datePart, timePart });
    if (datePart !== lastValue.datePart || timePart !== lastValue.timePart) {
        setLastValue({ datePart, timePart });
        setDate(datePart);
        setTime(timePart.slice(0, 5));
    }
    const emit = (nextDate, nextTime) => {
        if (DATE.test(nextDate) && TIME.test(nextTime))
            onChange(`${nextDate}T${nextTime}`);
    };
    return (_jsxs("div", { role: "group", "aria-label": ariaLabel, "aria-describedby": describedBy, className: cn('flex min-w-0 items-center gap-2', className), children: [_jsx(Input, { id: id, type: "date", "aria-label": ariaLabel ? `${ariaLabel} — ${t('kit.date')}` : t('kit.date'), min: min?.split('T')[0], max: max?.split('T')[0], disabled: disabled, invalid: invalid, className: "min-w-0 flex-1", value: date, onChange: (event) => {
                    setDate(event.target.value);
                    emit(event.target.value, time);
                } }), _jsx(TimeInput, { "aria-label": ariaLabel ? `${ariaLabel} — ${t('kit.time')}` : t('kit.time'), disabled: disabled, invalid: invalid, className: "w-20 shrink-0", value: time, onChange: (next) => {
                    setTime(next);
                    emit(date, next);
                } })] }));
}
//# sourceMappingURL=time-input.js.map